import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Checkbox, Divider, Form, Header, Input, Message } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import { API, showError, showSuccess } from '../../helpers';

const EditModelPrice = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const modelPriceId = params.id;
  const isEdit = modelPriceId !== undefined;
  const [loading, setLoading] = useState(isEdit);

  const originInputs = {
    model_id: '',
    sell_prompt_ratio: '1.0',
    sell_completion_ratio: '1.0',
    sell_cache_ratio: '0.5',
    cost_prompt_ratio: '1.0',
    cost_completion_ratio: '1.0',
    cost_cache_ratio: '0.5',
    peak_hour_enabled: false,
    peak_hour_multiplier: '2.0',
    peak_hour_period1_start: '9',
    peak_hour_period1_end: '12',
    peak_hour_period2_start: '14',
    peak_hour_period2_end: '18',
    remark: '',
  };
  const [inputs, setInputs] = useState(originInputs);

  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleCheckboxChange = (e, { name, checked }) => {
    setInputs((inputs) => ({ ...inputs, [name]: checked }));
  };

  const loadPrice = async () => {
    const res = await API.get(`/api/model_price/${modelPriceId}`);
    const { success, message, data } = res.data;
    if (success) {
      // 解析 peak_hour_config JSON
      let p1s = '9', p1e = '12', p2s = '14', p2e = '18';
      if (data.peak_hour_config) {
        try {
          const cfg = JSON.parse(data.peak_hour_config);
          p1s = String(cfg.period1_start ?? 9);
          p1e = String(cfg.period1_end ?? 12);
          p2s = String(cfg.period2_start ?? 14);
          p2e = String(cfg.period2_end ?? 18);
        } catch (e) { /* 解析失败使用默认值 */ }
      }
      setInputs({
        model_id: data.model_id || '',
        sell_prompt_ratio: String(data.sell_prompt_ratio ?? '1.0'),
        sell_completion_ratio: String(data.sell_completion_ratio ?? '1.0'),
        sell_cache_ratio: String(data.sell_cache_ratio ?? '0.5'),
        cost_prompt_ratio: String(data.cost_prompt_ratio ?? '1.0'),
        cost_completion_ratio: String(data.cost_completion_ratio ?? '1.0'),
        cost_cache_ratio: String(data.cost_cache_ratio ?? '0.5'),
        peak_hour_enabled: !!data.peak_hour_enabled,
        peak_hour_multiplier: String(data.peak_hour_multiplier ?? '2.0'),
        peak_hour_period1_start: p1s,
        peak_hour_period1_end: p1e,
        peak_hour_period2_start: p2s,
        peak_hour_period2_end: p2e,
        remark: data.remark || '',
      });
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isEdit) {
      loadPrice().then();
    }
  }, []);

  const submit = async () => {
    if (!inputs.model_id) {
      showError(t('model_price.model_id_required'));
      return;
    }
    // 将时段打包为 JSON
    const peakConfig = JSON.stringify({
      period1_start: parseInt(inputs.peak_hour_period1_start) || 0,
      period1_end: parseInt(inputs.peak_hour_period1_end) || 0,
      period2_start: parseInt(inputs.peak_hour_period2_start) || 0,
      period2_end: parseInt(inputs.peak_hour_period2_end) || 0,
    });
    const body = {
      model_id: inputs.model_id,
      sell_prompt_ratio: parseFloat(inputs.sell_prompt_ratio) || 1.0,
      sell_completion_ratio: parseFloat(inputs.sell_completion_ratio) || 1.0,
      sell_cache_ratio: parseFloat(inputs.sell_cache_ratio) || 0.5,
      cost_prompt_ratio: parseFloat(inputs.cost_prompt_ratio) || 1.0,
      cost_completion_ratio: parseFloat(inputs.cost_completion_ratio) || 1.0,
      cost_cache_ratio: parseFloat(inputs.cost_cache_ratio) || 0.5,
      peak_hour_enabled: inputs.peak_hour_enabled,
      peak_hour_multiplier: parseFloat(inputs.peak_hour_multiplier) || 2.0,
      peak_hour_config: peakConfig,
      remark: inputs.remark,
    };

    let res;
    if (isEdit) {
      body.id = parseInt(modelPriceId);
      res = await API.put('/api/model_price/', body);
    } else {
      res = await API.post('/api/model_price/', body);
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(isEdit ? t('model_price.update_success') : t('model_price.create_success'));
      navigate('/model_price');
    } else {
      showError(message);
    }
  };

  const handleCancel = () => {
    navigate('/model_price');
  };

  const ratioField = (name, labelKey, placeholder, hintKey) => (
    <Form.Field required key={name}>
      <label>{t(labelKey)}</label>
      <Input
        name={name}
        value={inputs[name]}
        onChange={handleInputChange}
        placeholder={placeholder}
        type='number'
        step='0.01'
      />
      <small style={{ color: '#888' }}>
        {t(hintKey)}
      </small>
    </Form.Field>
  );

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit ? t('model_price.edit_title') : t('model_price.add_title')}
          </Card.Header>
          {loading ? (
            <Message info>{t('model_price.loading')}</Message>
          ) : (
            <Form>
              <Form.Field required>
                <label>{t('model_price.field.model_id')}</label>
                <Input
                  name='model_id'
                  value={inputs.model_id}
                  onChange={handleInputChange}
                  placeholder='gpt-3.5-turbo'
                  disabled={isEdit}
                />
              </Form.Field>

              <Divider horizontal>
                <Header as='h4' color='blue'>{t('model_price.section.sell')}</Header>
              </Divider>

              {ratioField('sell_prompt_ratio', 'model_price.field.sell_prompt_ratio', '1.0', 'model_price.hint.sell_prompt_ratio')}
              {ratioField('sell_completion_ratio', 'model_price.field.sell_completion_ratio', '1.0', 'model_price.hint.sell_completion_ratio')}
              {ratioField('sell_cache_ratio', 'model_price.field.sell_cache_ratio', '0.5', 'model_price.hint.sell_cache_ratio')}

              <Divider horizontal>
                <Header as='h4' color='orange'>{t('model_price.section.cost')}</Header>
              </Divider>

              {ratioField('cost_prompt_ratio', 'model_price.field.cost_prompt_ratio', '1.0', 'model_price.hint.cost_prompt_ratio')}
              {ratioField('cost_completion_ratio', 'model_price.field.cost_completion_ratio', '1.0', 'model_price.hint.cost_completion_ratio')}
              {ratioField('cost_cache_ratio', 'model_price.field.cost_cache_ratio', '0.5', 'model_price.hint.cost_cache_ratio')}

              <Divider horizontal>
                <Header as='h4' color='red'>高峰计费配置</Header>
              </Divider>

              <Form.Field>
                <Checkbox
                  label='启用高峰计费（仅此模型，独立于全局设置）'
                  name='peak_hour_enabled'
                  checked={inputs.peak_hour_enabled}
                  onChange={handleCheckboxChange}
                />
              </Form.Field>

              {inputs.peak_hour_enabled && (
                <>
                  <Form.Field>
                    <label>高峰倍率</label>
                    <Input
                      name='peak_hour_multiplier'
                      value={inputs.peak_hour_multiplier}
                      onChange={handleInputChange}
                      placeholder='2.0'
                      type='number'
                      step='0.1'
                      min='1'
                    />
                    <small style={{ color: '#888' }}>高峰时段计费倍数，如 2.0 表示双倍计费</small>
                  </Form.Field>

                  <Form.Group widths='equal'>
                    <Form.Field>
                      <label>时段1 开始（北京时间，0-23）</label>
                      <Input
                        name='peak_hour_period1_start'
                        value={inputs.peak_hour_period1_start}
                        onChange={handleInputChange}
                        placeholder='9'
                        type='number'
                        min='0'
                        max='23'
                      />
                    </Form.Field>
                    <Form.Field>
                      <label>时段1 结束（不含，北京时间）</label>
                      <Input
                        name='peak_hour_period1_end'
                        value={inputs.peak_hour_period1_end}
                        onChange={handleInputChange}
                        placeholder='12'
                        type='number'
                        min='0'
                        max='23'
                      />
                    </Form.Field>
                  </Form.Group>

                  <Form.Group widths='equal'>
                    <Form.Field>
                      <label>时段2 开始（0表示不配置）</label>
                      <Input
                        name='peak_hour_period2_start'
                        value={inputs.peak_hour_period2_start}
                        onChange={handleInputChange}
                        placeholder='14'
                        type='number'
                        min='0'
                        max='23'
                      />
                    </Form.Field>
                    <Form.Field>
                      <label>时段2 结束（0表示不配置）</label>
                      <Input
                        name='peak_hour_period2_end'
                        value={inputs.peak_hour_period2_end}
                        onChange={handleInputChange}
                        placeholder='18'
                        type='number'
                        min='0'
                        max='23'
                      />
                    </Form.Field>
                  </Form.Group>
                  <small style={{ color: '#888' }}>
                    示例：时段1 9~12 表示 9:00～11:59 为高峰；时段2 14~18 表示 14:00～17:59 为高峰。时段2 start=end=0 表示只有一个时段。
                  </small>
                </>
              )}

              <Form.Field>
                <label>{t('model_price.field.remark')}</label>
                <Input
                  name='remark'
                  value={inputs.remark}
                  onChange={handleInputChange}
                  placeholder={t('model_price.field.remark_placeholder')}
                />
              </Form.Field>
              <Button color='green' onClick={submit}>
                {t('model_price.submit')}
              </Button>
              <Button onClick={handleCancel}>
                {t('model_price.cancel')}
              </Button>
            </Form>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditModelPrice;
